import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { rateWithCommas } from 'utils/NumberCommaSeprator';

const formatINR = (value) => `₹ ${(Number(value) || 0).toLocaleString('en-IN')}`;
// Mirrors `_rate` in client_invoice_service.py, which formats this same column on the
// invoice PDF — the web preview and the PDF must show the same rate.
const formatRate = (value) => `₹ ${rateWithCommas(value)}`;
const formatCount = (value) => (Number(value) || 0).toLocaleString('en-IN');

const MonthBlock = styled.div`
	margin-bottom: 16px;
`;

const Heading = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #18405d;
	margin-bottom: 6px;
`;

const Note = styled.div`
	font-size: 12px;
	color: #8c8c8c;
	margin-top: 8px;
`;

// Shaped like the tax summary table it sits under, so the two read as one pair.
const columns = [
	{
		title: 'Range',
		key: 'range',
		render: (_, r) => `${formatCount(r.slab_min)} – ${formatCount(r.slab_max)}`,
	},
	{ title: 'Rate', dataIndex: 'rate_per_order', render: formatRate },
	{ title: 'Count', dataIndex: 'shipment_count', render: formatCount },
	{ title: 'Amount', dataIndex: 'amount', render: formatINR },
];

/**
 * Slab-wise shipment revenue for a warehousing invoice — how the shipment count
 * behind the warehousing line item was billed across the project's slabs.
 *
 * Rows come from the backend's revenue engine, never recomputed here. Renders
 * nothing for a Cost Plus project (no slabs) or when the figures are unavailable,
 * so callers can drop it in unconditionally.
 */
/** One month's slab walk. Months are never summed — see InvoiceSlabTable below. */
const renderMonth = (month) => {
	const rows = month.slab_breakdown || [];
	const billed = rows.reduce((sum, r) => sum + (Number(r.shipment_count) || 0), 0);
	const total = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
	const shipments = Number(month.shipment_count) || 0;
	const period = month.period_month
		? moment(month.period_month, ['YYYY-MM-DD', 'YYYY-MM']).format('MMMM YYYY')
		: '';
	// Volume past the top slab earns nothing, so the rows can account for fewer
	// shipments than were recorded. Say so rather than letting the totals differ.
	const unbilled = Math.max(0, shipments - billed);

	return (
		<MonthBlock key={month.period_month || 'single'}>
			<Heading>
				{period || 'Shipment Slabs'} — {formatCount(shipments)} shipments
			</Heading>
			<Table
				rowKey={(record, index) => `slab-${index}`}
				size="small"
				columns={columns}
				dataSource={rows}
				pagination={false}
				// antd's summary API takes a render function by design
				// eslint-disable-next-line react/no-unstable-nested-components
				summary={() => (
					<Table.Summary.Row>
						<Table.Summary.Cell index={0} colSpan={2}>
							<b>Total</b>
						</Table.Summary.Cell>
						<Table.Summary.Cell index={1}>
							<b>{formatCount(billed)}</b>
						</Table.Summary.Cell>
						<Table.Summary.Cell index={2}>
							<b>{formatINR(total)}</b>
						</Table.Summary.Cell>
					</Table.Summary.Row>
				)}
			/>
			{unbilled > 0 && (
				<Note>
					{formatCount(unbilled)} shipment{unbilled === 1 ? '' : 's'} above the top slab
					earned nothing.
				</Note>
			)}
		</MonthBlock>
	);
};

/**
 * Slab-wise shipment revenue for a warehousing project — one table per month it
 * shipped in, each priced on its own.
 *
 * Months are deliberately not combined: slab capacity resets every month, so
 * summing the counts across months would push the volume into the cheaper upper
 * slabs and understate the revenue. Rows come from the backend's revenue engine
 * and are never recomputed here.
 *
 * Renders nothing for a Cost Plus project (no slabs) or when there are no
 * shipments, so callers can drop it in unconditionally.
 */
export default function InvoiceSlabTable({ warehouseContext }) {
	// Invoices raised before the per-month snapshot existed carry a single walk.
	const months =
		warehouseContext?.monthly_breakdowns?.length > 0
			? warehouseContext.monthly_breakdowns
			: [warehouseContext].filter((c) => c?.slab_breakdown?.length > 0);
	if (months.length === 0) {
		return null;
	}

	const grandTotal = months.reduce(
		(sum, m) => sum + (m.slab_breakdown || []).reduce((t, r) => t + (Number(r.amount) || 0), 0),
		0
	);
	// The line item is priced from `revenue`, which also carries Cross Dock charges
	// and ad-hoc adjustments; the slabs only explain the shipment portion.
	const extras =
		Number(warehouseContext.revenue || 0) - Number(warehouseContext.base_revenue || 0);

	return (
		<>
			{months.map(renderMonth)}
			{months.length > 1 && (
				<Note style={{ marginTop: 0, fontWeight: 600, color: '#18405d' }}>
					All months — {formatINR(grandTotal)}
				</Note>
			)}
			{Math.round(extras) !== 0 && (
				<Note>
					Plus {formatINR(extras)} of additional charges and adjustments, billed on top of
					the slabs.
				</Note>
			)}
		</>
	);
}

InvoiceSlabTable.propTypes = {
	warehouseContext: PropTypes.object,
};

InvoiceSlabTable.defaultProps = {
	warehouseContext: null,
};
